import React from "react";
import { Box, Typography, Button, keyframes } from "@mui/material";

// Import combined founder image
import CombinedFoundersImage from "../assets/founders/founders.png";

// Subtle badge float animation
const badgeFloat = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-4px); }
`;

// Gradient shift for background
const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

// Subtle float animation for info cards
const cardFloat = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
`;

const SupportSection = () => {

  const supportFeatures = [
    {
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="3" y="4" width="18" height="18" rx="2" stroke="#407BFF" strokeWidth="2" />
          <line x1="3" y1="10" x2="21" y2="10" stroke="#407BFF" strokeWidth="2" />
          <line x1="9" y1="4" x2="9" y2="10" stroke="#407BFF" strokeWidth="2" />
          <line x1="15" y1="4" x2="15" y2="10" stroke="#407BFF" strokeWidth="2" />
          <circle cx="7" cy="14" r="1" fill="#407BFF" />
          <circle cx="12" cy="14" r="1" fill="#407BFF" />
          <circle cx="17" cy="14" r="1" fill="#407BFF" />
          <circle cx="7" cy="18" r="1" fill="#407BFF" />
          <circle cx="12" cy="18" r="1" fill="#407BFF" />
        </svg>
      ),
      title: "24/7 Support",
    },
    {
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="8" r="4" stroke="#407BFF" strokeWidth="2" />
          <path d="M4 20C4 16.6863 7.58172 14 12 14C16.4183 14 20 16.6863 20 20" stroke="#407BFF" strokeWidth="2" strokeLinecap="round" />
          <path d="M16 6L18 4M18 4L20 6M18 4V8" stroke="#407BFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
      title: "Dedicated Success Manager",
    },
    {
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="3" stroke="#407BFF" strokeWidth="2" />
          <path d="M12 2V4M12 20V22M2 12H4M20 12H22" stroke="#407BFF" strokeWidth="2" strokeLinecap="round" />
          <path d="M4.93 4.93L6.34 6.34M17.66 17.66L19.07 19.07M4.93 19.07L6.34 17.66M17.66 6.34L19.07 4.93" stroke="#407BFF" strokeWidth="2" strokeLinecap="round" />
        </svg>
      ),
      title: "Ongoing Optimization",
    },
  ];

  // LinkedIn Icon Component
  const LinkedInIcon = ({ size = 20 }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"
        fill="currentColor"
      />
    </svg>
  );

  return (
    <Box
      sx={{
        width: "100%",
        padding: {
          xs: "40px 16px",
          sm: "50px 24px",
          md: "60px 64px",
        },
        backgroundColor: "transparent",
      }}
    >
      {/* Main Container with Animated Gradient Background */}
      <Box
        sx={{
          background: "linear-gradient(135deg, rgba(199, 218, 255, 0.5) 0%, rgba(220, 232, 255, 0.3) 25%, rgba(199, 218, 255, 0.5) 50%, rgba(230, 240, 255, 0.4) 75%, rgba(199, 218, 255, 0.5) 100%)",
          backgroundSize: "400% 400%",
          animation: `${gradientShift} 15s ease infinite`,
          borderRadius: { xs: "24px", md: "40px" },
          padding: {
            xs: "40px 20px",
            sm: "50px 30px",
            md: "70px 60px 90px",
          },
          maxWidth: "1200px",
          margin: "0 auto",
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: "-50%",
            right: "-10%",
            width: "300px",
            height: "300px",
            background: "radial-gradient(circle, rgba(53,103,214,0.08) 0%, transparent 70%)",
            borderRadius: "50%",
          },
          "&::after": {
            content: '""',
            position: "absolute",
            bottom: "-30%",
            left: "-5%",
            width: "250px",
            height: "250px",
            background: "radial-gradient(circle, rgba(53,103,214,0.06) 0%, transparent 70%)",
            borderRadius: "50%",
          },
        }}
      >
        {/* Section Header */}
        <Box
          sx={{
            textAlign: "center",
            marginBottom: { xs: "30px", md: "40px" },
          }}
        >
          <Typography
            sx={{
              fontFamily: "Poppins, sans-serif",
              fontSize: { xs: "24px", sm: "32px", md: "42px" },
              fontWeight: 600,
              lineHeight: { xs: "32px", sm: "40px", md: "52px" },
              color: "#000",
              marginBottom: "0px",
            }}
          >
            We don't just onboard you
          </Typography>
          <Typography
            sx={{
              fontFamily: "Poppins, sans-serif",
              fontSize: { xs: "24px", sm: "32px", md: "42px" },
              fontWeight: 600,
              lineHeight: { xs: "32px", sm: "40px", md: "52px" },
              color: "#407BFF",
              marginBottom: { xs: "16px", md: "24px" },
            }}
          >
            we stay with you
          </Typography>
          <Typography
            sx={{
              fontFamily: "Inter, sans-serif",
              fontSize: { xs: "14px", sm: "16px", md: "18px" },
              fontWeight: 500,
              lineHeight: { xs: "22px", md: "28px" },
              color: "#4B5563",
              maxWidth: "700px",
              margin: "0 auto",
            }}
          >
            Our support team stays continuously engaged monitoring, assisting,
            and helping you scale through every stage.
          </Typography>
        </Box>

        {/* Support Features Badges - Animated */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: "12px", md: "16px" },
            justifyContent: "center",
            alignItems: "center",
            marginBottom: { xs: "50px", md: "70px" },
            flexWrap: "wrap",
            position: "relative",
            zIndex: 1,
          }}
        >
          {supportFeatures.map((feature, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                backgroundColor: "rgba(255,255,255,0.95)",
                borderRadius: "50px",
                padding: { xs: "12px 20px", md: "14px 26px" },
                boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.06)",
                border: "1px solid rgba(64, 123, 255, 0.12)",
                minWidth: { xs: "auto", sm: "auto" },
                cursor: "default",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                animation: `${badgeFloat} 3s ease-in-out infinite`,
                animationDelay: `${index * 0.5}s`,
                "&:hover": {
                  transform: "translateY(-4px) scale(1.02)",
                  boxShadow: "0px 8px 24px rgba(64, 123, 255, 0.15)",
                  borderColor: "rgba(64, 123, 255, 0.25)",
                  "& .support-icon": {
                    transform: "scale(1.1)",
                  },
                },
              }}
            >
              <Box
                className="support-icon"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "transform 0.3s ease",
                }}
              >
                {feature.icon}
              </Box>
              <Typography
                sx={{
                  fontFamily: "Inter, sans-serif",
                  fontSize: { xs: "13px", md: "14px" },
                  fontWeight: 600,
                  color: "#1e293b",
                  whiteSpace: "nowrap",
                }}
              >
                {feature.title}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* FOUNDER LAYOUT - Cards on Left and Right of Image */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "1fr 1.3fr 1fr",
            },
            gap: { xs: "32px", md: "40px" },
            alignItems: "center",
            position: "relative",
            zIndex: 1,
            maxWidth: "1100px",
            margin: "0 auto",
          }}
        >
          {/* LEFT CARD - Joyneel Acharya */}
          <Box
            sx={{
              backgroundColor: "rgba(255, 255, 255, 0.95)",
              borderRadius: "18px",
              padding: { xs: "20px 18px", md: "24px 22px" },
              boxShadow: "0px 6px 24px rgba(0, 0, 0, 0.08)",
              border: "1px solid rgba(64, 123, 255, 0.12)",
              transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
              animation: `${cardFloat} 4s ease-in-out infinite`,
              order: { xs: 2, md: 1 },
              "&:hover": {
                transform: "translateY(-6px)",
                boxShadow: "0px 12px 36px rgba(64, 123, 255, 0.18)",
                borderColor: "rgba(64, 123, 255, 0.25)",
              },
            }}
          >
            <Typography
              sx={{
                fontFamily: "Poppins, sans-serif",
                fontSize: { xs: "20px", sm: "22px", md: "24px" },
                fontWeight: 700,
                color: "#407BFF",
                marginBottom: "8px",
                lineHeight: 1.2,
                textAlign: "center",
              }}
            >
              Joyneel Acharya
            </Typography>

            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                marginBottom: "12px",
              }}
            >
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  backgroundColor: "rgba(2, 38, 116, 0.1)",
                  borderRadius: "8px",
                  padding: "4px 10px",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Inter, sans-serif",
                    fontSize: { xs: "10px", md: "11px" },
                    fontWeight: 600,
                    color: "#022674",
                    letterSpacing: "0.5px",
                  }}
                >
                  CO-FOUNDER
                </Typography>
              </Box>
            </Box>

            <Box sx={{ marginBottom: "14px", textAlign: "center" }}>
              <Typography
                sx={{
                  fontFamily: "Inter, sans-serif",
                  fontSize: { xs: "12px", md: "13px" },
                  fontWeight: 500,
                  color: "#374151",
                  lineHeight: 1.5,
                  marginBottom: "3px",
                }}
              >
                BITS Pilani
              </Typography>
              <Typography
                sx={{
                  fontFamily: "Inter, sans-serif",
                  fontSize: { xs: "12px", md: "13px" },
                  fontWeight: 500,
                  color: "#374151",
                  lineHeight: 1.5,
                }}
              >
                MBA (IIM Calcutta)
              </Typography>
            </Box>

            <Button
              onClick={() => window.open("https://www.linkedin.com/in/joyneel-acharya/", "_blank", "noopener,noreferrer")}
              startIcon={<LinkedInIcon size={14} />}
              sx={{
                backgroundColor: "#0077b5",
                color: "#ffffff",
                fontFamily: "Inter, sans-serif",
                fontSize: { xs: "11px", md: "12px" },
                fontWeight: 600,
                textTransform: "none",
                borderRadius: "8px",
                padding: { xs: "8px 14px", md: "9px 16px" },
                width: "100%",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                boxShadow: "0 3px 10px rgba(0, 119, 181, 0.3)",
                "&:hover": {
                  backgroundColor: "#005885",
                  transform: "translateY(-2px)",
                  boxShadow: "0 5px 14px rgba(0, 119, 181, 0.4)",
                },
                "& .MuiButton-startIcon": {
                  marginRight: "6px",
                },
              }}
              aria-label="Connect with Joyneel Acharya on LinkedIn"
            >
              Connect on LinkedIn
            </Button>
          </Box>

          {/* CENTER - Combined Founder Image */}
          <Box
            sx={{
              position: "relative",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              order: { xs: 1, md: 2 },
            }}
          >
            {/* Decorative floating circles */}
            <Box
              sx={{
                position: "absolute",
                top: { xs: "-8px", md: "-12px" },
                right: { xs: "8%", md: "-12px" },
                width: { xs: "36px", md: "48px" },
                height: { xs: "36px", md: "48px" },
                borderRadius: "50%",
                backgroundColor: "rgba(64, 123, 255, 0.15)",
                animation: `${cardFloat} 3s ease-in-out infinite`,
                zIndex: 0,
              }}
            />
            <Box
              sx={{
                position: "absolute",
                bottom: { xs: "-8px", md: "-12px" },
                left: { xs: "8%", md: "-12px" },
                width: { xs: "32px", md: "40px" },
                height: { xs: "32px", md: "40px" },
                borderRadius: "50%",
                backgroundColor: "rgba(2, 38, 116, 0.12)",
                animation: `${cardFloat} 3.5s ease-in-out infinite`,
                animationDelay: "0.5s",
                zIndex: 0,
              }}
            />

            <Box
              sx={{
                position: "relative",
                width: "100%",
                maxWidth: { xs: "260px", sm: "300px", md: "360px" },
                aspectRatio: "1/1",
                borderRadius: "28px",
                overflow: "hidden",
                boxShadow: "0 16px 48px rgba(0, 0, 0, 0.12)",
                border: "5px solid rgba(255, 255, 255, 0.95)",
                transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                backgroundColor: "#E8F2FF",
                zIndex: 1,
                "&:hover": {
                  transform: "scale(1.03)",
                  boxShadow: "0 20px 60px rgba(64, 123, 255, 0.2)",
                  borderColor: "rgba(255, 255, 255, 1)",
                  "& img": {
                    transform: "scale(1.05)",
                  },
                },
              }}
            >
              <Box
                component="img"
                src={CombinedFoundersImage}
                alt="Joyneel Acharya and Shweta Gandhi - Founders of NiroGGyan"
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  objectPosition: "center",
                  transition: "transform 0.4s ease",
                }}
              />

              {/* Overlay gradient for polish */}
              <Box
                sx={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: "35%",
                  background: "linear-gradient(to top, rgba(2, 38, 116, 0.12), transparent)",
                  pointerEvents: "none",
                }}
              />
            </Box>
          </Box>

          {/* RIGHT CARD - Shweta Gandhi */}
          <Box
            sx={{
              backgroundColor: "rgba(255, 255, 255, 0.95)",
              borderRadius: "18px",
              padding: { xs: "20px 18px", md: "24px 22px" },
              boxShadow: "0px 6px 24px rgba(0, 0, 0, 0.08)",
              border: "1px solid rgba(64, 123, 255, 0.12)",
              transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
              animation: `${cardFloat} 4s ease-in-out infinite`,
              animationDelay: "0.5s",
              order: { xs: 3, md: 3 },
              "&:hover": {
                transform: "translateY(-6px)",
                boxShadow: "0px 12px 36px rgba(64, 123, 255, 0.18)",
                borderColor: "rgba(64, 123, 255, 0.25)",
              },
            }}
          >
            <Typography
              sx={{
                fontFamily: "Poppins, sans-serif",
                fontSize: { xs: "20px", sm: "22px", md: "24px" },
                fontWeight: 700,
                color: "#407BFF",
                marginBottom: "8px",
                lineHeight: 1.2,
                textAlign: "center",
              }}
            >
              Shweta Gandhi
            </Typography>

            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                marginBottom: "12px",
              }}
            >
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  backgroundColor: "rgba(2, 38, 116, 0.1)",
                  borderRadius: "8px",
                  padding: "4px 10px",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Inter, sans-serif",
                    fontSize: { xs: "10px", md: "11px" },
                    fontWeight: 600,
                    color: "#022674",
                    letterSpacing: "0.5px",
                  }}
                >
                  CO-FOUNDER
                </Typography>
              </Box>
            </Box>

            <Box sx={{ marginBottom: "14px", textAlign: "center" }}>
              <Typography
                sx={{
                  fontFamily: "Inter, sans-serif",
                  fontSize: { xs: "12px", md: "13px" },
                  fontWeight: 500,
                  color: "#374151",
                  lineHeight: 1.5,
                  marginBottom: "3px",
                }}
              >
                B.Pharm
              </Typography>
              <Typography
                sx={{
                  fontFamily: "Inter, sans-serif",
                  fontSize: { xs: "12px", md: "13px" },
                  fontWeight: 500,
                  color: "#374151",
                  lineHeight: 1.5,
                }}
              >
                MBA (IIM Indore)
              </Typography>
            </Box>

            <Button
              onClick={() => window.open("https://www.linkedin.com/in/shweta-gandhi/", "_blank", "noopener,noreferrer")}
              startIcon={<LinkedInIcon size={14} />}
              sx={{
                backgroundColor: "#0077b5",
                color: "#ffffff",
                fontFamily: "Inter, sans-serif",
                fontSize: { xs: "11px", md: "12px" },
                fontWeight: 600,
                textTransform: "none",
                borderRadius: "8px",
                padding: { xs: "8px 14px", md: "9px 16px" },
                width: "100%",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                boxShadow: "0 3px 10px rgba(0, 119, 181, 0.3)",
                "&:hover": {
                  backgroundColor: "#005885",
                  transform: "translateY(-2px)",
                  boxShadow: "0 5px 14px rgba(0, 119, 181, 0.4)",
                },
                "& .MuiButton-startIcon": {
                  marginRight: "6px",
                },
              }}
              aria-label="Connect with Shweta Gandhi on LinkedIn"
            >
              Connect on LinkedIn
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default SupportSection;